import { Link } from "react-router-dom";
import { ArrowLeft, User } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { EmployeeAvatar } from "../components/EmployeeAvatar";
import { StatusBadge } from "../components/StatusBadge";
import { getStoredAccountType } from "../auth";
import { useEmployeeDetail } from "../hooks/useEnterpriseEmployeeDetail";
import { formatFamilyRelationship } from "../lib/familyRelationships";
import { formatEmployeePosition } from "../lib/employeePositions";
import { getEmployeeHospitalEntries } from "../lib/employeeHospitals";

type EnterpriseEmployeeDetailPageProps = {
  employeeId: number;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR");
}

function formatGender(value: string | null | undefined): string {
  if (value === "M") return "Masculin";
  if (value === "F") return "Féminin";
  return value || "—";
}

export function EnterpriseEmployeeDetailPage({ employeeId }: EnterpriseEmployeeDetailPageProps) {
  // Le compte Assurance ne gère pas les photos des assurés.
  const isInsurer = getStoredAccountType() === "enterprise";
  const isAdmin = getStoredAccountType() === "admin";
  const { employee, loading, error } = useEmployeeDetail(employeeId);

  if (loading) {
    return (
      <div className="theme-card p-8 text-center text-sm text-muted">
        Chargement du profil assuré…
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="theme-card p-8 text-center">
        <p className="text-sm text-muted">{error ?? "Assuré introuvable."}</p>
        <Link
          to="/dashboard/employees"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary"
        >
          <ArrowLeft size={14} />
          Retour à la liste
        </Link>
      </div>
    );
  }

  const familyMembers = employee.family_members ?? [];
  const hospitalEntries = getEmployeeHospitalEntries(employee);
  const homeHref = isAdmin ? "/dashboard/admin" : "/dashboard/enterprise";

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref={homeHref}
          items={[
            { label: "Gestion des assurés" },
            { label: "Liste des assurés", href: "/dashboard/employees" },
            { label: employee.name },
          ]}
        />
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/employees"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
          >
            <ArrowLeft size={15} />
            Retour
          </Link>
        </div>
      </div>

      <div className="theme-card p-6">
        <div className="mb-6 flex flex-wrap items-start gap-4">
          {isInsurer ? null : <EmployeeAvatar name={employee.name} photo={employee.photo} size={72} rounded="md" />}
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{employee.name}</h1>
            <div className="mt-2 flex flex-wrap gap-2">
              <StatusBadge variant="purple">{employee.system_id}</StatusBadge>
              <StatusBadge variant={employee.status === "active" ? "success" : "neutral"}>
                {employee.status === "active" ? "ACTIF" : "INACTIF"}
              </StatusBadge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <section>
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <User size={16} className="text-primary" />
              Informations personnelles
            </h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Matricule entreprise</dt>
                <dd className="mt-1 text-foreground">{employee.company_matricule || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Date de naissance</dt>
                <dd className="mt-1 text-foreground">{formatDate(employee.birthdate)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Sexe</dt>
                <dd className="mt-1 text-foreground">{formatGender(employee.gender)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Téléphone</dt>
                <dd className="mt-1 text-foreground">{employee.phone || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Email</dt>
                <dd className="mt-1 text-foreground">{employee.email || "—"}</dd>
              </div>
            </dl>
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold text-foreground">Affectation</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Entreprise</dt>
                <dd className="mt-1 text-foreground">{employee.company || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Poste</dt>
                <dd className="mt-1 text-foreground">{formatEmployeePosition(employee.position)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Antenne</dt>
                <dd className="mt-1 text-foreground">{employee.branch || "Non renseignée"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Province</dt>
                <dd className="mt-1 text-foreground">{employee.province || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Ville</dt>
                <dd className="mt-1 text-foreground">{employee.city || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {hospitalEntries.length > 1 ? "Hôpitaux affectés" : "Hôpital"}
                </dt>
                <dd className="mt-1 text-foreground">
                  {hospitalEntries.length === 0 ? (
                    "Non affecté"
                  ) : (
                    <ul className="space-y-1">
                      {hospitalEntries.map((hospital, index) => (
                        <li key={hospital.hospital_id ?? `${hospital.name}-${index}`} className="text-sm">
                          {hospital.name}
                          {hospital.is_primary ? (
                            <span className="ml-2 text-xs font-medium text-primary">(principal)</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </div>

      <div className="theme-card mt-5 overflow-hidden">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-foreground">Ayants droit ({familyMembers.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {isInsurer ? null : (
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    Photo
                  </th>
                )}
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">Nom</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">Lien</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">Naissance</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">Sexe</th>
              </tr>
            </thead>
            <tbody>
              {familyMembers.length === 0 ? (
                <tr>
                  <td colSpan={isInsurer ? 4 : 5} className="px-4 py-8 text-center text-sm text-muted">
                    Aucun ayant droit enregistré.
                  </td>
                </tr>
              ) : (
                familyMembers.map((member) => (
                  <tr key={member.id} className="border-b border-border last:border-0">
                    {isInsurer ? null : (
                      <td className="px-4 py-3">
                        <EmployeeAvatar
                          name={member.name}
                          photo={member.photo}
                          size={40}
                          rounded="md"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-foreground">{member.name}</td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {formatFamilyRelationship(member.relationship)}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatDate(member.birthdate)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{formatGender(member.gender)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
